import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, TextField, MenuItem, Card, CardContent } from '@mui/material';
import { ArrowBack, CloudUpload } from '@mui/icons-material';
import { getBrands, getProducts, updateCampaignById, getCampaignById, getCampaignFlowById } from '../services/api';
import { toast } from 'react-toastify';
import { openSignedUrl } from '../utils/s3';

function StageDetails() {
    const { id, stageId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const stageNameFromState = location.state?.stageName || '';
    const campaignTitleFromState = location.state?.campaignTitle || '';

    const [brands, setBrands] = useState([]);
    const [products, setProducts] = useState([]);
    const [campaignFlow, setCampaignFlow] = useState({});
    const [formData, setFormData] = useState({
        brand: '',
        product: '',
        campaignTitle: campaignTitleFromState,
        description: '',
        campaignBrief: null
    });
    const [errors, setErrors] = useState({});
    const [campaign, setCampaign] = useState(null);
    const [showSubmitButton, setShowSubmitButton] = useState(true);

    useEffect(() => {
        fetchCampaignFlowData();
        fetchBrandsAndProducts();
        fetchCampaignData();        
    }, []);

    const fetchBrandsAndProducts = async () => {
        try {
            const [brandsRes, productsRes] = await Promise.all([
                getBrands(),
                getProducts()
            ]);
            setBrands(brandsRes.data);
            setProducts(productsRes.data);
        } catch (error) {
            console.error(error);
        }
    };
    const fetchCampaignFlowData = async () => {
        try {
            const response = await getCampaignFlowById(stageId);
            console.log(response.data.data, 'response.data.data');
            const flowData = response.data.data;
            setCampaignFlow(flowData);
            setShowSubmitButton(flowData.status !== 1);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchCampaignData = async () => {
        try {
            const response = await getCampaignById(id);
            const campaignData = response.data.data;
            setCampaign(campaignData);

            setFormData(prev => ({
                ...prev,
                brand: campaignData.brand?._id || '',
                product: campaignData.product?._id || '',
                campaignTitle: campaignData.campaignName || prev.campaignTitle,
                description: campaignData.description || '',
                campaignBrief: campaignData.strategyDocument || campaignData.campaignBrief || null
            }));
        } catch (error) {
            console.error(error);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            const file = e.target.files[0];
            if (file.type !== 'application/pdf') {
                toast.error('Only PDF files are allowed');
                return;
            }
            setFormData({ ...formData, campaignBrief: file });
        }
    };

    const handleSubmit = async () => {
        const newErrors = {};
        if (!formData.brand) newErrors.brand = 'Brand is required';
        if (!formData.product) newErrors.product = 'Product is required';
        if (!formData.campaignBrief) newErrors.campaignBrief = 'Campaign Brief (PDF) is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        const submitData = new FormData();
        submitData.append('brand', formData.brand);
        submitData.append('product', formData.product);
        submitData.append('description', formData.description);
        submitData.append('stageStatus', 2);
        submitData.append('status', 1);
        submitData.append('stageId', stageId);
        if (formData.campaignBrief) {
            submitData.append('strategyDocument', formData.campaignBrief);
        }

        await updateCampaignById(id, submitData);
        setErrors({});
        navigate(`/marketing/edit/${id}`);
        toast.success('Submitted successfully');
    };

    return (
        <Box sx={{
            p: 3,
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            bgcolor: '#f8fafc' // Light background
        }}>

            <Box sx={{ width: '100%', maxWidth: 800, mb: 4, display: 'flex', alignItems: 'center' }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate(`/marketing/edit/${id}`)}
                    sx={{ mr: 2, color: 'text.secondary' }}
                >
                    Back
                </Button>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#333', flexGrow: 1, textAlign: 'center', ml: -10 }}>
                    Stage Details
                </Typography>
            </Box>

            <Card sx={{ maxWidth: 800, width: '100%', boxShadow: '0px 4px 20px rgba(0,0,0,0.05)', borderRadius: 3 }}>
                <CardContent sx={{ p: 5 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                        Campaign Information
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                        <Box sx={{ display: 'flex', gap: 3 }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>Brand Name</Typography>
                                <TextField
                                    select
                                    fullWidth
                                    value={formData.brand}
                                    onChange={(e) => {
                                        setFormData({ ...formData, brand: e.target.value });
                                        if (errors.brand) setErrors({ ...errors, brand: '' });
                                    }}
                                    error={!!errors.brand}
                                    helperText={errors.brand}
                                    sx={{ bgcolor: 'white' }}
                                    disabled={!showSubmitButton}
                                    required
                                >
                                    {brands.map((brand) => (
                                        <MenuItem key={brand._id} value={brand._id}>
                                            {brand.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>

                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>Product Name</Typography>
                                <TextField
                                    select
                                    fullWidth
                                    value={formData.product}
                                    onChange={(e) => {
                                        setFormData({ ...formData, product: e.target.value });
                                        if (errors.product) setErrors({ ...errors, product: '' });
                                    }}
                                    error={!!errors.product}
                                    helperText={errors.product}
                                    sx={{ bgcolor: 'white' }}
                                    disabled={!showSubmitButton}
                                    required
                                >
                                    {products.map((product) => (
                                        <MenuItem key={product._id} value={product._id}>
                                            {product.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>Campaign Name</Typography>
                            <TextField
                                fullWidth
                                value={formData.campaignTitle}
                                onChange={(e) => {
                                    setFormData({ ...formData, campaignTitle: e.target.value });
                                    if (errors.campaignTitle) setErrors({ ...errors, campaignTitle: '' });
                                }}
                                error={!!errors.campaignTitle}
                                // helperText={errors.campaignTitle || "Showing Campaign Name"}
                                sx={{ bgcolor: 'white' }}
                                disabled
                            />
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>Campaign Brief</Typography>
                            {showSubmitButton ? (
                                <Box
                                    sx={{
                                        border: `1px dashed ${errors.campaignBrief ? '#d32f2f' : '#cbd5e1'}`,
                                        borderRadius: 2,
                                        p: 3,
                                        textAlign: 'center',
                                        bgcolor: '#f1f5f9',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            bgcolor: '#e2e8f0',
                                            borderColor: '#94a3b8'
                                        }
                                    }}
                                    onClick={() => document.getElementById('stage-file-input').click()}
                                >
                                    <CloudUpload sx={{ fontSize: 32, color: '#64748b', mb: 1 }} />
                                    <Typography fontWeight="500" color="text.secondary">
                                        {formData.campaignBrief ? (typeof formData.campaignBrief === 'string' ? 'Campaign Brief Uploaded' : formData.campaignBrief.name) : 'Upload Campaign Brief (PDF)'}
                                    </Typography>
                                    <input
                                        id="stage-file-input"
                                        type="file"
                                        accept="application/pdf"
                                        hidden
                                        onChange={(e) => {
                                            handleFileChange(e);
                                            if (errors.campaignBrief) setErrors({ ...errors, campaignBrief: '' });
                                        }}
                                    />
                                </Box>
                            ) : (
                                formData.campaignBrief && (
                                    <Button
                                        variant="contained"
                                        onClick={() => openSignedUrl(formData.campaignBrief)}
                                        sx={{
                                            borderColor: '#1E90FF',
                                            textTransform: 'none',
                                        }}
                                    >
                                        Campaign Brief Document
                                    </Button>
                                )
                            )}
                            {showSubmitButton && (
                                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                    <Typography variant="caption" sx={{ bgcolor: '#e2e8f0', px: 1, py: 0.5, borderRadius: 1, color: '#475569' }}>
                                        Accepted: .PDF
                                    </Typography>
                                </Box>
                            )}
                            {errors.campaignBrief && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                    {errors.campaignBrief}
                                </Typography>
                            )}
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>Optional Notes</Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="Add any relevant notes or context for the approvers..."
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                sx={{ bgcolor: 'white' }}
                                disabled={!showSubmitButton}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                                variant="text"
                                sx={{ mr: 2, color: 'text.secondary' }}
                                onClick={() => navigate(`/marketing/edit/${id}`)}
                            >
                                Back
                            </Button>
                            {showSubmitButton && (
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={handleSubmit}
                                    sx={{
                                        bgcolor: '#1E90FF',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 4
                                    }}
                                >
                                    Submit for Approval
                                </Button>
                            )}
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}

export default StageDetails;
